import { Collection } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import CollectionName from '../../utils/collections';

/**
 * Every challenge config whose `field` equals `value`.
 *
 * @param {string} field - The challenge config field to match.
 * @param {string} value - The value to match.
 * @returns {Promise<Array<ChallengeConfig>>} The challenge configs found.
 */
const getChallengeConfigsBy = async (field, value) => {
  const challengeConfigCollection = new Collection(CollectionName.CHALLENGE_CONFIG, {
    // eslint-disable-next-line no-use-before-define
    createDocument: (source, opts) => new ChallengeConfig(source.id, opts),
    query: (ref) => ref.where(field, '==', value),
  });
  await challengeConfigCollection.fetch();
  return challengeConfigCollection.docs;
};

/**
 * ChallengeConfig model for storing challenge configuration data
 *
 * @class ChallengeConfig
 * @extends BaseDocument
 */
class ChallengeConfig extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CHALLENGE_CONFIG}/${id}`, opts);
  }

  /**
   * Get the meal plan ID associated with this challenge
   * @returns {string} The meal plan ID
   */
  get mealPlanId() {
    return this.data.mealPlanId || '';
  }

  /**
   * Get the program ID associated with this challenge
   * @returns {string} The program ID
   */
  get programId() {
    return this.data.programId || '';
  }

  /**
   * Get the tag ID associated with this challenge
   * @returns {string} The tag ID
   */
  get tag() {
    return this.data.tag || '';
  }

  /**
   * Creates a new challenge config document with given data.
   *
   * @param {object} data - New challenge config data containing mealPlanId and programId.
   * @returns {Promise<ChallengeConfig>} New challenge config document.
   */
  static async addDoc(data) {
    const challengeConfigCollection = new Collection(CollectionName.CHALLENGE_CONFIG);
    const doc = await challengeConfigCollection.add({
      ...data,
      createdAt: new Date(),
    });
    const challengeConfig = new ChallengeConfig(doc.id);
    await challengeConfig.init();
    return challengeConfig;
  }

  /**
   * Get a challenge config by its id.
   *
   * @param {string} id - The id of the challenge config to get.
   * @returns {Promise<ChallengeConfig>} The challenge config.
   */
  static async getChallengeConfigById(id) {
    const challengeConfig = new ChallengeConfig(id);
    await challengeConfig.init();
    return challengeConfig.exists ? challengeConfig : null;
  }

  /**
   * Get the challenge configs a program belongs to. A program can be attached to
   * several challenges.
   *
   * @param {string} programId - The id of the program.
   * @returns {Promise<Array<ChallengeConfig>>} The challenge configs using the program.
   */
  static getChallengeConfigsByProgramId(programId) {
    return getChallengeConfigsBy('programId', programId);
  }

  /**
   * Get the challenge configs a meal plan belongs to. A meal plan can be attached
   * to several challenges.
   *
   * @param {string} mealPlanId - The id of the meal plan.
   * @returns {Promise<Array<ChallengeConfig>>} The challenge configs using the meal plan.
   */
  static getChallengeConfigsByMealPlanId(mealPlanId) {
    return getChallengeConfigsBy('mealPlanId', mealPlanId);
  }
}

export default ChallengeConfig;
